/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useState} from 'react'

import PropTypes from 'prop-types'

/* -----------------Adyen End ------------------------ */
import {Alert, AlertIcon, Box, Container, Grid, GridItem, Stack} from '@chakra-ui/react'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import isEmpty from 'lodash/isEmpty'

/* -----------------Adyen End ------------------------ */
import {TranslationProvider} from '@epmc-mach/accelarator/overrides/app/context/translation-context'
import {useCurrentBasket} from '@epmc-mach/accelarator/overrides/app/hooks/use-current-basket'
import Payment from '@epmc-mach/accelarator/overrides/app/pages/checkout/partials/payment'

/* -----------------Adyen Begin ------------------------ */
import '/overrides/app/adyen/style/adyenCheckout.css'
import {trackEvent} from '../../utils/gtm'
import {CheckoutProvider, useCheckout} from '../checkout/utils/checkout-context'
import ShippingAddress from './partials/addresses/shipping'
import {CheckoutAddressesProvider} from './partials/checkout-addresses-context'
import {SeoContainer} from './partials/seo-container'
/* -----------------Adyen Begin ------------------------ */
import OrderSummary from '/overrides/app/components/order-summary'
import {useEmptyBasketCartRedirect} from '/overrides/app/hooks/use-empty-basket-cart-redirect'
import ContactInfo from '/overrides/app/pages/checkout/partials/contact-info'
import {useSourceCodeValidate} from '../../hooks/use-source-code-validate'

const Checkout = () => {
    const {data: basket} = useCurrentBasket()
    const {step} = useCheckout()
    const [error] = useState()

    useSourceCodeValidate(basket?.c_sourceCode)
    useEmptyBasketCartRedirect()

    useEffect(() => {
        if (
            error ||
            (step === 4 &&
                !document.querySelector(
                    '.adyen-checkout__dropin, .adyen-checkout__card, [data-cse]'
                ))
        ) {
            window.scrollTo({top: 0})
        }
    }, [error, step])

    useEffect(() => {
        trackEvent('checkout', {
            ecommerce: {
                checkout: {
                    actionField: {
                        step: 1,
                        option: '',
                        action: 'checkout'
                    },
                    products: []
                }
            }
        })
    }, [])

    return (
        <Box background="gray.50" flex="1">
            <Container
                data-testid="sf-checkout-container"
                maxWidth="container.xl"
                py={{base: 7, lg: 16}}
                px={{base: 0, lg: 8}}
            >
                {!isEmpty(basket) && (
                    <Grid templateColumns={{base: '1fr', lg: '60% 1fr'}} gap={{base: 10, xl: 16}}>
                        <GridItem>
                            <Stack spacing={4}>
                                {error && (
                                    <Alert status="error" variant="left-accent">
                                        <AlertIcon />
                                        {error}
                                    </Alert>
                                )}

                                <ContactInfo />
                                <ShippingAddress />
                                <Payment />
                            </Stack>
                        </GridItem>

                        <GridItem py={6} px={[4, 4, 4, 0]}>
                            <OrderSummary
                                basket={basket}
                                showTaxEstimationForm={false}
                                showCartItems={true}
                            />
                        </GridItem>
                    </Grid>
                )}
            </Container>
        </Box>
    )
}

const CheckoutContainer = () => {
    return (
        <TranslationProvider>
            <SeoContainer />
            <CheckoutAddressesProvider>
                <CheckoutProvider>
                    <Checkout />
                </CheckoutProvider>
            </CheckoutAddressesProvider>
        </TranslationProvider>
    )
}

Checkout.propTypes = {
    basket: PropTypes.object
}

CheckoutContainer.propTypes = {}

export default CheckoutContainer
